import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Save, Eye, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin';

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
});

const defaultPrivacyContent = `<h2>Introduction</h2>
<p>Fallen Dragon Events is committed to protecting and respecting your privacy. This Privacy Policy outlines how we collect, use, store, and disclose your personal information in compliance with the General Data Protection Regulation (GDPR).</p>

<h2>Data We Collect</h2>
<p>To facilitate bookings and provide a safe experience, we collect and process the following personal information:</p>
<ul>
<li><strong>Booking Information:</strong> When you make a booking, we collect your name, email address, and home address to confirm and manage your reservation.</li>
<li><strong>Liability Waiver Information:</strong> We require all participants to sign a liability waiver to participate in our activities. This waiver includes your name, signature, and may also collect other relevant information necessary for the waiver's purpose.</li>
</ul>

<h2>How We Use Your Data</h2>
<p>We use your personal data to:</p>
<ul>
<li>Process and manage bookings for our events and activities.</li>
<li>Communicate with you about your booking or relevant updates regarding your participation.</li>
<li>Ensure compliance with safety regulations and provide a secure environment for all participants.</li>
</ul>
<p>We will not use your personal information for any marketing purposes unless you have explicitly consented.</p>

<h2>Data Storage and Security</h2>
<ul>
<li>Booking and liability waiver information is securely stored to ensure compliance with data protection standards.</li>
<li>We implement appropriate organisational and technical measures to safeguard your data and protect it from unauthorised access, alteration, or disclosure.</li>
</ul>

<h2>Data Retention</h2>
<p>We retain personal data only as long as necessary to fulfil the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements.</p>

<h2>Data Sharing</h2>
<p>We do not share your personal data with third parties unless it is legally required or necessary for booking and event management purposes.</p>

<h2>Your Rights</h2>
<p>Under the GDPR, you have the following rights concerning your data:</p>
<ul>
<li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
<li><strong>Rectification:</strong> Request corrections to inaccurate or incomplete personal data.</li>
<li><strong>Erasure:</strong> Request deletion of personal data under certain conditions.</li>
<li><strong>Restriction:</strong> Request limitations on how we process your personal data.</li>
<li><strong>Objection:</strong> Object to the processing of your personal data under certain conditions.</li>
<li><strong>Data Portability:</strong> Request the transfer of your personal data to another organisation or to yourself.</li>
</ul>

<h2>Changes to This Privacy Policy</h2>
<p>We reserve the right to update this Privacy Policy periodically. Changes will be posted on this page, and the effective date updated. By continuing to use our services, you agree to the terms of this Privacy Policy.</p>`;

const defaultTermsContent = `<h2>Acceptance of Terms</h2>
<p>By accessing and using this website and our services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our website or services.</p>

<h2>Services Description</h2>
<p>Fallen Dragon Events provides specialist airsoft events and hard LARP experiences. All activities involve physical exertion and inherent risks that participants must acknowledge.</p>

<h2>Booking and Payment</h2>
<ul>
<li>All bookings are subject to availability and confirmation.</li>
<li>Payment must be made in full at the time of booking unless otherwise specified.</li>
<li>Prices are displayed in GBP and include VAT where applicable.</li>
</ul>

<h2>Cancellation Policy</h2>
<ul>
<li>Cancellations made more than 14 days before the event date will receive a full refund.</li>
<li>Cancellations made between 7-14 days before the event will receive a 50% refund.</li>
<li>Cancellations made less than 7 days before the event are non-refundable.</li>
<li>We reserve the right to cancel events due to weather conditions or insufficient numbers, in which case full refunds will be provided.</li>
</ul>

<h2>Participant Requirements</h2>
<ul>
<li>All participants must be at least 18 years of age unless specifically stated otherwise for particular events.</li>
<li>Participants must complete and sign a liability waiver before taking part in any activities.</li>
<li>Participants must follow all safety instructions provided by our staff.</li>
<li>Appropriate clothing and footwear must be worn at all times.</li>
</ul>

<h2>Liability Disclaimer</h2>
<p>Participation in airsoft and LARP activities carries inherent risks including but not limited to physical injury. By booking and participating in our events, you acknowledge these risks and agree to release Fallen Dragon Events from any liability for injuries sustained during activities, provided reasonable safety measures have been observed.</p>

<h2>Code of Conduct</h2>
<p>All participants are expected to:</p>
<ul>
<li>Treat other participants and staff with respect.</li>
<li>Follow all game rules and safety guidelines.</li>
<li>Report any safety concerns immediately to staff.</li>
<li>Not attend events under the influence of alcohol or drugs.</li>
</ul>
<p>We reserve the right to remove any participant who violates these guidelines without refund.</p>

<h2>Intellectual Property</h2>
<p>All content on this website, including text, images, logos, and videos, is the property of Fallen Dragon Events unless otherwise stated. Unauthorised use is prohibited.</p>

<h2>Governing Law</h2>
<p>These Terms and Conditions are governed by the laws of the United Kingdom. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>

<h2>Changes to Terms</h2>
<p>We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the new terms.</p>`;

const defaultGeneralRulesContent = `<h2>Safety Rules</h2>
<p>Safety is our top priority at all Fallen Dragon Events. All participants must adhere to the following safety guidelines:</p>
<ul>
<li>Eye protection must be worn at all times during gameplay.</li>
<li>Barrel socks/plugs must be used in safe zones.</li>
<li>No blind firing around corners or through gaps.</li>
<li>Call your hits honestly and immediately.</li>
</ul>

<h2>Equipment Rules</h2>
<ul>
<li>All replica weapons must be chronographed before use.</li>
<li>Maximum FPS limits apply to all weapon types.</li>
<li>No modified or prohibited ammunition types.</li>
<li>Melee weapons must be foam/rubber only.</li>
</ul>

<h2>Conduct Rules</h2>
<ul>
<li>Respect all staff, marshals, and fellow participants.</li>
<li>No aggressive behaviour, verbal abuse, or unsportsmanlike conduct.</li>
<li>Follow all marshal instructions immediately.</li>
<li>Report any safety concerns or rule violations to staff.</li>
</ul>

<h2>Site Rules</h2>
<ul>
<li>No smoking except in designated areas.</li>
<li>No alcohol or drugs on site.</li>
<li>Keep the site clean - take your rubbish with you.</li>
<li>Respect site boundaries and restricted areas.</li>
</ul>

<h2>Consequences</h2>
<p>Violation of these rules may result in warnings, removal from the event, or permanent bans depending on severity. No refunds will be given for rule violations.</p>`;

const AdminLegalPages = () => {
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy-policy');
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    'privacy-policy': {
      title: 'Privacy Policy',
      content: defaultPrivacyContent,
      meta_description: 'Fallen Dragon Events Privacy Policy - Learn how we collect, use, store, and protect your personal information.',
      company_name: 'Fallen Dragon Events',
      company_email: '',
      company_phone: '',
      company_address: '',
      company_registration: '',
      effective_date: new Date().toISOString().split('T')[0]
    },
    'terms': {
      title: 'Terms & Conditions',
      content: defaultTermsContent,
      meta_description: 'Fallen Dragon Events Terms and Conditions - Read our terms of service, booking policy, and participant requirements.',
      company_name: 'Fallen Dragon Events',
      company_email: '',
      company_phone: '',
      company_address: '',
      company_registration: '',
      effective_date: new Date().toISOString().split('T')[0]
    },
    'general-rules': {
      title: 'General Rules',
      content: defaultGeneralRulesContent,
      meta_description: 'Fallen Dragon Events General Rules - Important rules and guidelines for all participants at our airsoft and LARP events.',
      company_name: 'Fallen Dragon Events',
      company_email: '',
      company_phone: '',
      company_address: '',
      company_registration: '',
      effective_date: new Date().toISOString().split('T')[0]
    }
  });

  useEffect(() => {
    fetchLegalPages();
  }, []);

  const fetchLegalPages = async () => {
    try {
      const response = await axios.get(`${API}/legal-pages`, getAuthHeader());
      if (response.data.success && response.data.pages) {
        const loadedPages = {};
        response.data.pages.forEach(page => {
          loadedPages[page.page_type] = page;
        });
        
        // Merge with defaults
        setFormData(prev => ({
          'privacy-policy': loadedPages['privacy-policy'] || prev['privacy-policy'],
          'terms': loadedPages['terms'] || prev['terms'],
          'general-rules': loadedPages['general-rules'] || prev['general-rules']
        }));
        setPages(loadedPages);
      }
    } catch (error) {
      console.error('Error fetching legal pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = formData[activeTab];
      const pageExists = pages[activeTab];
      
      if (pageExists) {
        // Update existing
        await axios.put(`${API}/legal-pages/${activeTab}`, data, getAuthHeader());
      } else {
        // Create new
        await axios.post(`${API}/legal-pages`, {
          ...data,
          page_type: activeTab
        }, getAuthHeader());
      }
      
      toast.success(`${activeTab === 'privacy-policy' ? 'Privacy Policy' : 'Terms & Conditions'} saved successfully`);
      fetchLegalPages();
    } catch (error) {
      console.error('Error saving legal page:', error);
      toast.error('Failed to save legal page');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentData = formData[activeTab];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#c5bfa9]">Legal Pages</h1>
          <p className="text-gray-600 mt-1">Manage your Privacy Policy, Terms & Conditions, and General Rules</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPreview(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#3d4a35] hover:bg-[#4a5d42] text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('privacy-policy')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'privacy-policy'
              ? 'text-[#3d4a35] border-b-2 border-[#3d4a35]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Privacy Policy
        </button>
        <button
          onClick={() => setActiveTab('terms')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'terms'
              ? 'text-[#3d4a35] border-b-2 border-[#3d4a35]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Terms & Conditions
        </button>
        <button
          onClick={() => setActiveTab('general-rules')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'general-rules'
              ? 'text-[#3d4a35] border-b-2 border-[#3d4a35]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          General Rules
        </button>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Page Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
          <input
            type="text"
            value={currentData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
          />
        </div>

        {/* Meta Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description (SEO)</label>
          <input
            type="text"
            value={currentData.meta_description || ''}
            onChange={(e) => handleInputChange('meta_description', e.target.value)}
            placeholder="Brief description for search engines"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
          />
        </div>

        {/* Company Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input
              type="text"
              value={currentData.company_name || ''}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Registration</label>
            <input
              type="text"
              value={currentData.company_registration || ''}
              onChange={(e) => handleInputChange('company_registration', e.target.value)}
              placeholder="e.g., Company 12345678"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
            <input
              type="email"
              value={currentData.company_email || ''}
              onChange={(e) => handleInputChange('company_email', e.target.value)}
              placeholder="info@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
            <input
              type="text"
              value={currentData.company_phone || ''}
              onChange={(e) => handleInputChange('company_phone', e.target.value)}
              placeholder="e.g., 07123 456789"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
          <input
            type="text"
            value={currentData.company_address || ''}
            onChange={(e) => handleInputChange('company_address', e.target.value)}
            placeholder="Full address"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
          <input
            type="date"
            value={currentData.effective_date || ''}
            onChange={(e) => handleInputChange('effective_date', e.target.value)}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
          />
        </div>

        {/* Content Editor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Page Content
            <span className="text-xs text-gray-500 ml-2">(HTML supported: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;)</span>
          </label>
          <textarea
            value={currentData.content || ''}
            onChange={(e) => handleInputChange('content', e.target.value)}
            rows={20}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e] font-mono text-sm"
          />
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1a1a1a] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">{currentData.title} - Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              <div className="max-w-3xl mx-auto">
                <h1 className="text-3xl font-bold text-[#c9a55a] mb-6">{currentData.title}</h1>
                <div 
                  className="prose prose-invert prose-lg max-w-none
                    prose-headings:text-[#c9a55a] prose-headings:font-semibold prose-headings:mt-8 prose-headings:mb-4
                    prose-p:text-gray-300 prose-p:leading-relaxed
                    prose-li:text-gray-300
                    prose-strong:text-white
                    prose-a:text-[#c9a55a] prose-a:no-underline hover:prose-a:underline"
                  dangerouslySetInnerHTML={{ __html: currentData.content }}
                />
                {currentData.effective_date && (
                  <p className="text-gray-500 mt-8 pt-4 border-t border-gray-700">
                    Effective Date: {new Date(currentData.effective_date).toLocaleDateString('en-GB')}
                  </p>
                )}
                {currentData.company_name && (
                  <div className="mt-8 pt-4 border-t border-gray-700 text-gray-500">
                    <p>© {currentData.company_name}</p>
                    {currentData.company_registration && <p>{currentData.company_registration}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLegalPages;
